import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ExpertProfileFilters } from '../types/expert-profile';

const STORAGE_KEY = 'sparkadmin_expert_profile_filters';

const DEFAULT_FILTERS: ExpertProfileFilters = {
  status: '',
  page: 1,
  pageSize: 25,
  sortBy: 'name',
  sortOrder: 'asc',
};

function parseFiltersFromURL(searchParams: URLSearchParams): Partial<ExpertProfileFilters> {
  const filters: Partial<ExpertProfileFilters> = {};

  const status = searchParams.get('status');
  if (status && ['active', 'inactive'].includes(status)) {
    filters.status = status as ExpertProfileFilters['status'];
  }

  const page = searchParams.get('page');
  if (page) filters.page = parseInt(page, 10);

  const sortBy = searchParams.get('sortBy');
  if (sortBy && ['name', 'createdAt'].includes(sortBy)) {
    filters.sortBy = sortBy as ExpertProfileFilters['sortBy'];
  }

  const sortOrder = searchParams.get('sortOrder');
  if (sortOrder && ['asc', 'desc'].includes(sortOrder)) {
    filters.sortOrder = sortOrder as ExpertProfileFilters['sortOrder'];
  }

  return filters;
}

function filtersToSearchParams(filters: ExpertProfileFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.status) params.set('status', filters.status);
  if (filters.page !== 1) params.set('page', filters.page.toString());
  if (filters.sortBy !== 'name') params.set('sortBy', filters.sortBy);
  if (filters.sortOrder !== 'asc') params.set('sortOrder', filters.sortOrder);

  return params;
}

function loadFiltersFromStorage(): Partial<ExpertProfileFilters> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveFiltersToStorage(filters: ExpertProfileFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

export function useExpertProfileFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<ExpertProfileFilters>(() => {
    const urlFilters = parseFiltersFromURL(searchParams);
    const storageFilters = loadFiltersFromStorage();
    return {
      ...DEFAULT_FILTERS,
      ...storageFilters,
      ...urlFilters,
    };
  });

  const updateFilter = useCallback(
    (key: keyof ExpertProfileFilters, value: ExpertProfileFilters[keyof ExpertProfileFilters]) => {
      setFilters((prev) => {
        const updated = { ...prev, [key]: value };
        if (key !== 'page') {
          updated.page = 1;
        }
        const params = filtersToSearchParams(updated);
        setSearchParams(params, { replace: true });
        saveFiltersToStorage(updated);
        return updated;
      });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams(new URLSearchParams(), { replace: true });
    saveFiltersToStorage(DEFAULT_FILTERS);
  }, [setSearchParams]);

  const hasActiveFilters = filters.status !== '';

  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
  };
}
